/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { Checkbox } from "primereact/checkbox";
import { Tag } from "primereact/tag";
import { ProgressBar } from "primereact/progressbar";
import { accessAPI } from "../../../shared";

// Definición de los campos del análisis de riesgo
const camposAnalisis = [
  {
    key: "testReport",
    etiqueta: "El producto tiene un reporte de ensayo"
  },
  {
    key: "laboratorioAcreditado",
    etiqueta: "El laboratorio emisor se encuentra acreditado para la norma",
    dependeDe: "testReport"
  },
  {
    key: "testReportPosteriorAcreditacion",
    etiqueta:
      "El reporte de ensayo es posterior a la fecha de evidencia de acreditación",
    dependeDe: "testReport"
  },
  {
    key: "marcaAcreditacionEnTR",
    etiqueta: "El reporte de ensayo cuenta con la marca de acreditación",
    dependeDe: "testReport"
  },
  {
    key: "certificado",
    etiqueta: "El producto tiene un certificado de conformidad"
  },
  {
    key: "OCPAcreditado",
    etiqueta: "El OCP emisor se encuentra acreditado para la norma",
    dependeDe: "certificado"
  },
  {
    key: "certificadoPosteriorAcreditacion",
    etiqueta:
      "El certificado es posterior a la fecha de evidencia de acreditación",
    dependeDe: "certificado"
  },
  {
    key: "marcaAcreditacionEnCertificado",
    etiqueta: "El certificado cuenta con marca de acreditación",
    dependeDe: "certificado"
  }
];

export default function AnalisisRiesgo({ actividad, onActualizado, toast }) {
  const [estadoAnalisis, setEstadoAnalisis] = useState({});
  const [riesgoTotal, setRiesgoTotal] = useState(null);
  const [loaderActualizar, setLoaderActualizar] = useState(false);

  // Cargar análisis de riesgo de la actividad
  useEffect(() => {
    if (actividad) {
      const analisis = actividad.analisisriesgo || {};
      setEstadoAnalisis(analisis);
      setRiesgoTotal(
        analisis.hasOwnProperty("riesgoTotal") ? analisis.riesgoTotal : null
      );
    }
  }, [actividad]);

  // Manejar cambio de checkbox y actualizar automáticamente
  const handleCheckboxChange = (campo, valor) => {
    const nuevoEstado = { ...estadoAnalisis, [campo]: valor };

    // Si se desactiva un campo padre, desactivar también los dependientes
    if (!valor) {
      camposAnalisis.forEach((c) => {
        if (c.dependeDe === campo) {
          nuevoEstado[c.key] = false;
        }
      });
    }

    setEstadoAnalisis(nuevoEstado);

    // Actualizar automáticamente en el servidor
    actualizarAnalisisRiesgo(nuevoEstado);
  };

  // Verificar si un campo está deshabilitado
  const campoDeshabilitado = (campo) => {
    if (!campo.dependeDe) return false;
    return !estadoAnalisis[campo.dependeDe];
  };

  // Actualizar análisis de riesgo
  function actualizarAnalisisRiesgo(analisisParaEnviar) {
    if (!actividad) return;

    const datosAnalisis = analisisParaEnviar || estadoAnalisis;

    setLoaderActualizar(true);
    accessAPI(
      "PUT",
      `admin/actividad/analisisriesgo/${actividad.id}`,
      { analisisriesgo: datosAnalisis },
      (respuesta) => {
        setLoaderActualizar(false);
        setRiesgoTotal(respuesta.calculoAnalisisRiesgo);

        // Notificar al componente padre
        if (onActualizado) {
          onActualizado({
            ...datosAnalisis,
            riesgoTotal: respuesta.calculoAnalisisRiesgo
          });
        }
      },
      (error) => {
        setLoaderActualizar(false);
        toast?.current?.show({
          severity: "error",
          summary: "Error",
          detail: error[0]?.msg || "Error al actualizar el análisis de riesgo",
          sticky: true
        });
      }
    );
  }

  // Calcular progreso del riesgo (mayor riesgo = más llena la barra)
  const calcularProgreso = () => {
    if (riesgoTotal === null) return 0;
    const progreso = Math.max(0, Math.min(100, riesgoTotal / 2));
    return progreso;
  };

  return (
    <div>
      <p
        css={{
          color: "var(--grisTextos)",
          fontSize: "0.9rem",
          marginBottom: "1rem"
        }}
      >
        Resultado numérico en función de las características de la documentación
        técnica presentada.
      </p>

      {/* Lista de checkboxes */}
      <div
        css={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginBottom: "1rem"
        }}
      >
        {camposAnalisis.map((campo) => (
          <div
            key={campo.key}
            css={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem",
              backgroundColor: campoDeshabilitado(campo)
                ? "var(--surface-100)"
                : "var(--surface-50)",
              borderRadius: "4px",
              opacity: campoDeshabilitado(campo) ? 0.6 : 1,
              marginLeft: campo.dependeDe ? "1rem" : 0
            }}
          >
            <Checkbox
              checked={estadoAnalisis[campo.key] || false}
              onChange={(e) => handleCheckboxChange(campo.key, e.checked)}
              disabled={campoDeshabilitado(campo) || loaderActualizar}
            />
            <span
              css={{
                fontSize: "0.85rem",
                color: campoDeshabilitado(campo)
                  ? "var(--grisTextos)"
                  : "inherit"
              }}
            >
              {campo.etiqueta}
            </span>
          </div>
        ))}
      </div>

      {/* Resultado del riesgo */}
      {riesgoTotal !== null && (
        <div
          css={{
            padding: "1rem",
            backgroundColor:
              riesgoTotal < 100
                ? "rgba(76, 175, 80, 0.1)"
                : "rgba(244, 67, 54, 0.1)",
            borderRadius: "6px",
            marginBottom: "1rem"
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "0.5rem"
            }}
          >
            <span css={{ fontWeight: "500", fontSize: "0.9rem" }}>
              Valor de riesgo:
            </span>
            {loaderActualizar ? (
              <div css={{ height: "2.1em" }}>
                <i className="pi pi-spin pi-spinner" />
              </div>
            ) : (
              <Tag
                value={riesgoTotal}
                severity={riesgoTotal < 100 ? "success" : "danger"}
              />
            )}
          </div>
          <ProgressBar
            value={calcularProgreso()}
            showValue={false}
            css={{
              height: "6px",
              ".p-progressbar-value": {
                backgroundColor:
                  riesgoTotal < 100 ? "var(--verde)" : "var(--rojo)"
              }
            }}
          />
          <p
            css={{
              marginTop: "0.5rem",
              fontSize: "0.8rem",
              color: "var(--grisTextos)"
            }}
          >
            {riesgoTotal < 100
              ? "Riesgo menor a 100: hay suficientes garantías para emitir."
              : "Riesgo igual o mayor a 100: el riesgo es demasiado grande para emitir."}
          </p>
        </div>
      )}

      {riesgoTotal === null && (
        <div
          css={{
            padding: "0.75rem",
            backgroundColor: "var(--surface-100)",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          {loaderActualizar ? (
            <>
              <i
                className="pi pi-spin pi-spinner"
                css={{ fontSize: "1rem", color: "var(--grisTextos)" }}
              />
              <span css={{ color: "var(--grisTextos)", fontSize: "0.85rem" }}>
                Calculando...
              </span>
            </>
          ) : (
            <p
              css={{
                color: "var(--grisTextos)",
                margin: 0,
                fontSize: "0.85rem"
              }}
            >
              Riesgo aún no calculado. Seleccione los indicadores para calcular.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
